import {
  focusEditor,
  getEditorString,
  getNodeEntry,
  isCollapsed,
} from 'lib-knowledge-document-structure/plate/common';
import { createMyPluginFactory } from 'lib-knowledge-document-structure/plateFunctions';
import { MyEditor } from 'lib-knowledge-document-structure/plateTypes';
import {
  Path,
  Point,
  Range as SlateRange,
} from 'lib-knowledge-document-structure/slate/package';

import { Block } from './shortcut.blocks';
import { actions, shortcutState } from './shortcut.store';
import { runShortcut } from './shortcut.utils';

export const triggerShortcutMenu = ({
  editor,
  point,
  newLine,
  anchorElement,
}: {
  editor: MyEditor;
  point: Point;
  newLine: boolean;
  anchorElement?: HTMLElement;
}): void => {
  // Grab the full node, ignoring offset
  const node = getNodeEntry(editor, point.path);

  // Only show the shortcut menu for root level nodes.
  // P's are nested inside table cells and we don't want to show the shortcut menu for them.
  if (!node || node[1].length > 2) return;

  actions.show({ point, oldNodeState: node[0], newLine, anchorElement });
  focusEditor(editor, point);
};

export const onSelectBlock = (editor: MyEditor, block: Block): void => {
  if (!shortcutState.point) return;

  // Give the shortcut menu a chance to hide otherwise usePointHover
  // blows up trying to show the divider point.
  setTimeout(() => {
    // This should never happen.
    if (!shortcutState.point || !shortcutState.oldNodeState) return;

    runShortcut({
      editor,
      block,
      point: shortcutState.point,
      newLine: shortcutState.newLine,
      oldNodeState: shortcutState.oldNodeState,
    });

    actions.trigger();
  }, 0);

  focusEditor(editor);
};

// see https://plate.udecode.io/docs/plugins#handlers for a description on
// how event handlers flow and the use of it's return value.
export const createShortcutPlugin = createMyPluginFactory({
  key: 'shortcutPlugin',
  handlers: {
    onKeyDown: (editor) => (ev) => {
      if (ev.key === '/') {
        // If we already have the shortcut menu open, then lets close it
        if (shortcutState.point) {
          actions.hide();
        }

        // Trigger showing shortcutMenu at current selection
        const { selection } = editor;
        if (selection && isCollapsed(selection)) {
          // TODO: exclude if in some blocks...
          const [start] = SlateRange.edges(selection);
          triggerShortcutMenu({ editor, point: start, newLine: false });
          return true;
        }
      }

      // Now we are updating the shortcut menu, so if it's not shown bail
      if (!shortcutState.point) return;

      switch (ev.key) {
        case 'ArrowDown': {
          ev.preventDefault();
          actions.moveSelectionDown();
          return true;
        }
        case 'ArrowUp': {
          ev.preventDefault();
          actions.moveSelectionUp();
          return true;
        }
        case 'Tab':
        case 'Enter': {
          const selectedBlock =
            shortcutState.filteredBlocks[shortcutState.selectedBlockIndex];
          if (selectedBlock) {
            ev.preventDefault();
            onSelectBlock(editor, selectedBlock);
            return true;
          }
          actions.hide();
          return true;
        }
        case 'Escape': {
          ev.preventDefault();
          actions.hide();
          return true;
        }
      }
    },
    onBlur: () => () => actions.hide(),
    onChange: (editor) => () => {
      if (!editor.selection) return;
      if (!shortcutState.point) return;

      // Check we don't go back past the /
      if (
        shortcutState.search.startsWith('/') &&
        Point.isBefore(editor.selection.focus, {
          path: shortcutState.point.path,
          offset: shortcutState.point.offset + 1,
        })
      ) {
        actions.hide();
        return false;
      }

      // If we aren't in the same node then close the shortcut menu
      if (
        !Path.equals(
          [shortcutState.point.path[0]],
          [editor.selection.focus.path[0]],
        )
      ) {
        actions.hide();
        return false;
      }

      // Get the text from the anchor to where the user is currently focused.
      const r: SlateRange = {
        anchor: shortcutState.point,
        focus: editor.selection.focus,
      };
      const shortcutText = getEditorString(editor, r);
      actions.setSearch(shortcutText);
    },
  },
});
